import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { OverlayOptions } from "@helm/chart";
import { sessionOverlayActive } from "@helm/core/bars";
import { usePopover } from "../components/Popover";
import { Icon } from "../components/Icon";
import { OVERLAY_ITEMS } from "./overlayItems";
import { SessionOverlayMenu } from "./SessionOverlayMenu";

/**
 * Chart header "Layers" menu (the layers icon of the Glacis toolbar, mockups 01/02): switches for
 * position/order lines, key levels, the stats box and the liquidity heatmap, plus the session and
 * time overlay (B3: PD-C, named opens, killzones), saved with the panel.
 * Rendered into the owning document's body (fixed) so the dock's clipping never cuts it off; pop-out
 * windows get it in their own document.
 */
export function OverlayPicker({
  value,
  onChange,
}: {
  value: OverlayOptions;
  onChange: (o: OverlayOptions) => void;
}): React.JSX.Element {
  const pop = usePopover();
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const trigger = pop.triggerProps.ref;
  useLayoutEffect(() => {
    if (!pop.open) return;
    const r = trigger.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left, top: r.bottom + 4 });
  }, [pop.open, trigger]);
  const on = OVERLAY_ITEMS.filter((o) => value[o.key]).length + (sessionOverlayActive(value.session) ? 1 : 0);
  const doc = trigger.current?.ownerDocument ?? document;
  return (
    <>
      <button
        type="button"
        className={on > 1 ? "tf-layers active" : "tf-layers"}
        aria-haspopup="dialog"
        aria-label="Chart overlays"
        title="Overlays: position & order lines, key levels, stats box, liquidity heatmap, session & time"
        {...pop.triggerProps}
      >
        <Icon name="layers" size={12} />
      </button>
      {pop.open &&
        createPortal(
          <div
            className="popover overlay-menu"
            role="dialog"
            aria-label="Chart overlays"
            style={{ position: "fixed", left: pos.left, top: pos.top }}
            {...pop.panelProps}
          >
            {OVERLAY_ITEMS.map((o) => (
              <label key={o.key} className="check-row" title={o.hint}>
                <input
                  type="checkbox"
                  checked={value[o.key]}
                  onChange={(e) => {
                    onChange({ ...value, [o.key]: e.target.checked });
                  }}
                />
                <span>{o.label}</span>
              </label>
            ))}
            <SessionOverlayMenu
              value={value.session}
              onChange={(session) => {
                onChange({ ...value, session });
              }}
            />
          </div>,
          doc.body,
        )}
    </>
  );
}
